// 261015 P2b-3 F4 — "Chấp nhận (cảnh đã có chi tiết)…" ở ⑤ (C6): logic THUẦN (không import antd — luật jest của admin-presentation).
// BE là bên quyết (người = token, lý do 10–300, còn chip, hết hiệu lực khi cảnh/plant đổi); FE chỉ chọn đích, kiểm lý do, dựng body, hiển thị dòng "Đã chấp nhận".
import type { OutlineViolation } from '../../../types/adaptation';
import type { ProposeAcceptPlantBody } from '../../../types/adaptationAssistant';
import { formatShortDateTime } from '../adaptationUtils';
import { ACCEPT_REASON_INPUT_MAX, MAX_ACCEPT_REASON, MIN_ACCEPT_REASON, acceptReasonState } from './skeletonAcceptView';

export const PLANT_ACCEPT_GATE = 'PLANT_NOT_WRITTEN';
/** Cùng thước C6 của ② và BE (`MIN/MAX_PLANT_ACCEPT_REASON`). */
export const PLANT_ACCEPT_MIN = MIN_ACCEPT_REASON;
export const PLANT_ACCEPT_MAX = MAX_ACCEPT_REASON;
export const PLANT_ACCEPT_INPUT_MAX = ACCEPT_REASON_INPUT_MAX;

type V = Pick<OutlineViolation, 'gate' | 'severity' | 'kind' | 'refs' | 'superseded'>;
const squash = (s: unknown): string => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Nút chỉ cho CHIP lỗi thật: PLANT_NOT_WRITTEN WARN, `kind` vắng/defect, chưa bị thay thế, có ít nhất một beat. */
export const canOfferPlantAccept = (v: Partial<V> | null | undefined): boolean =>
  !!v && v.gate === PLANT_ACCEPT_GATE && v.severity === 'WARN' && (v.kind === undefined || v.kind === 'defect') && v.superseded !== true && plantAcceptTargets(v as V).length > 0;

/** Beat có thể chấp nhận của một hàng (không rỗng, không trùng, giữ thứ tự). */
export const plantAcceptTargets = (v: Pick<OutlineViolation, 'refs'> | null | undefined): string[] =>
  Array.from(new Set((Array.isArray(v?.refs) ? v!.refs : []).map((r) => squash(r)).filter(Boolean)));

export const plantAcceptReasonState = (reason: string) => acceptReasonState(reason);

export const canSubmitPlantAccept = (beatId: string, reason: string): boolean => !!squash(beatId) && acceptReasonState(reason).ok;

/** Body `propose-accept-plant`: lý do gộp khoảng trắng, cắt ≤ 300; BE tự lấy người từ token. */
export const buildProposeAcceptBody = (requestId: string, beatId: string, reason: string): ProposeAcceptPlantBody =>
  ({ requestId, beatId: squash(beatId), reason: squash(reason).slice(0, PLANT_ACCEPT_MAX) });

// ── khối "Đã chấp nhận" ở ⑤ ─────────────────────────────────────────────────────────────────

export interface AcceptedPlantRow { beatId: string; reason: string; by: string; byLabel: string; at: string; atText: string }

/** Mỗi mục `kind:'accepted'` của GET scripts (gate PLANT_NOT_WRITTEN, có `acceptance`) ⇒ một hàng cho mỗi beat. Thiếu `acceptance` / mã khác ⇒ bỏ (BE lạ không làm vỡ màn hình). */
export const acceptedPlantRows = (violations: ReadonlyArray<Partial<OutlineViolation> | null | undefined> | null | undefined): AcceptedPlantRow[] => {
  const rows: AcceptedPlantRow[] = [];
  for (const v of Array.isArray(violations) ? violations : []) {
    if (!v || v.kind !== 'accepted' || v.gate !== PLANT_ACCEPT_GATE || !v.acceptance) continue;
    for (const beatId of plantAcceptTargets(v as V)) {
      rows.push({
        beatId, reason: v.acceptance.reason, by: v.acceptance.by, byLabel: squash(v.acceptance.byName) || squash(v.acceptance.by), at: v.acceptance.at, atText: formatShortDateTime(v.acceptance.at),
      });
    }
  }
  return rows;
};

/** "Chi tiết gieo beat_9 — «lý do» — bởi Lan lúc 11/10 10:30". */
export const acceptedPlantLine = (r: AcceptedPlantRow): string =>
  `Chi tiết gieo ${r.beatId} — «${r.reason}» — bởi ${r.byLabel || 'không rõ'}${r.atText ? ` lúc ${r.atText}` : ''}`;

// ── lỗi từ route `propose-accept-plant` ─────────────────────────────────────────────────────

const CODE_TEXT: Record<string, string> = {
  REASON_INVALID: `Lý do cần ${PLANT_ACCEPT_MIN}–${PLANT_ACCEPT_MAX} ký tự — nói cụ thể cảnh nào đã có chi tiết gì.`,
  ACTOR_NOT_HUMAN: 'Chấp nhận phải do người đăng nhập bấm — phiên này không xác định được người thao tác.',
  NOTHING_TO_ACCEPT: 'Không còn cảnh báo “chưa có chi tiết gieo” cho chi tiết này (đã được giao, chưa kiểm, hoặc khung xương đã đổi) — tải lại tập.',
  THREAD_BUSY: 'Khung Trợ lý đang có một lượt chưa xong — đợi xong rồi thử lại.',
  OUTLINE_NOT_APPROVED: 'Khung xương chưa được duyệt — duyệt lại rồi mới chấp nhận được.',
  STAGE_NOT_SUPPORTED: 'Bước này chưa hỗ trợ chấp nhận chi tiết gieo.',
};

/** Câu thay thế khi BE không kèm message: theo `response.data.code`; lạ ⇒ câu chung. */
export const plantAcceptErrorText = (err: unknown): string => {
  const code = (err as { response?: { data?: { code?: unknown } } } | null | undefined)?.response?.data?.code;
  return (typeof code === 'string' && CODE_TEXT[code]) || 'Không tạo được đề xuất chấp nhận';
};
